import { diag, flush, logError, SID } from './log.ts';

/**
 * Starts on-device diagnostics (ADR-0024): app lifecycle, errors, the heartbeat to the watchdog worker, event-loop
 * lag, long frames where the browser reports them (Chromium LoAF), storage, and "the previous run ended badly".
 * Everything stays on the device until the user shares a report.
 */
const RUN_KEY = 'repwise.diag.run';
type Run = { sid: string; startedAt: number; lastBeat: number; clean: boolean; crumb?: string };
let crumb = 'start';
/** The last notable thing the app did, sent with each heartbeat so a hang record says what was happening. */
export function breadcrumb(c: string): void {
  crumb = c.slice(0, 80);
}

/** Live numbers for the debug panel. */
export const live = { lagMs: 0, maxLagMs: 0, beats: 0, lastHangMs: 0, watchdog: false };

let started = false;
export function startDiagnostics(build: string): void {
  if (started || typeof window === 'undefined') return;
  started = true;
  const read = (): Run | null => {
    try {
      return JSON.parse(localStorage.getItem(RUN_KEY) ?? 'null') as Run | null;
    } catch {
      return null;
    }
  };
  const write = (r: Run) => {
    try {
      localStorage.setItem(RUN_KEY, JSON.stringify(r));
    } catch {
      /* storage unavailable */
    }
  };
  // The previous run: no clean exit while it was beating recently = killed (by the OS, or force-quit during a hang).
  const prev = read();
  if (prev && !prev.clean) diag('app', 'previous_run_ended_unexpectedly', { sid: prev.sid, ranMs: prev.lastBeat - prev.startedAt, sinceLastBeatMs: Date.now() - prev.lastBeat, crumb: prev.crumb ?? '' }, 'warn');
  const run: Run = { sid: SID, startedAt: Date.now(), lastBeat: Date.now(), clean: false };
  write(run);

  const nav = navigator as Navigator & { deviceMemory?: number; connection?: { effectiveType?: string } };
  diag('app', 'start', {
    build,
    standalone: window.matchMedia?.('(display-mode: standalone)').matches ?? false,
    online: nav.onLine,
    cores: nav.hardwareConcurrency ?? null,
    memoryGb: nav.deviceMemory ?? null,
    net: nav.connection?.effectiveType ?? null,
    sw: !!nav.serviceWorker?.controller,
  });
  void nav.storage?.estimate?.().then((e) => diag('app', 'storage', { usageMb: (e.usage ?? 0) / 1e6, quotaMb: (e.quota ?? 0) / 1e6 })).catch(() => undefined);
  void nav.storage?.persisted?.().then((p) => diag('app', 'persisted', { persisted: p })).catch(() => undefined);

  window.addEventListener('error', (e) => logError('window', e.error ?? e.message));
  window.addEventListener('unhandledrejection', (e) => logError('promise', e.reason));
  window.addEventListener('online', () => diag('app', 'online'));
  window.addEventListener('offline', () => diag('app', 'offline'));
  document.addEventListener('visibilitychange', () => {
    diag('app', document.visibilityState === 'hidden' ? 'hidden' : 'visible');
    if (document.visibilityState === 'hidden') {
      dog?.postMessage({ type: 'hidden' });
      void flush();
    }
  });
  window.addEventListener('pagehide', () => {
    diag('app', 'pagehide');
    write({ ...run, lastBeat: Date.now(), clean: true, crumb });
    void flush();
  });
  window.addEventListener('pageshow', (e) => {
    if ((e as PageTransitionEvent).persisted) (diag('app', 'pageshow', { bfcache: true }), write({ ...run, clean: false }));
  });

  // The watchdog: a dedicated Worker that notices when these heartbeats stop.
  let dog: Worker | null = null;
  try {
    dog = new Worker(new URL('./watchdog.worker.ts', import.meta.url), { type: 'module' });
    dog.postMessage({ type: 'init', sid: SID });
    live.watchdog = true;
  } catch {
    diag('app', 'watchdog_unavailable', undefined, 'warn');
  }
  const BEAT = 500;
  let expected = performance.now() + BEAT;
  let n = 0;
  setInterval(() => {
    const now = performance.now();
    const lag = Math.max(0, now - expected);
    expected = now + BEAT;
    live.lagMs = Math.round(lag);
    live.maxLagMs = Math.max(live.maxLagMs, live.lagMs);
    live.beats++;
    // A stall too short for the watchdog but long enough to feel (a missed beat by > 300 ms)
    if (lag > 300 && document.visibilityState === 'visible') diag('perf', 'event_loop_lag', { ms: lag, crumb }, lag > 1000 ? 'warn' : 'info');
    dog?.postMessage({ type: 'beat', visible: document.visibilityState === 'visible', crumb });
    if (++n % 10 === 0) write({ ...run, lastBeat: Date.now(), clean: false, crumb });
  }, BEAT);

  // Chromium/Android: long animation frames with the script responsible (Safari has none of these).
  try {
    const po = new PerformanceObserver((list) => {
      for (const e of list.getEntries() as (PerformanceEntry & { blockingDuration?: number; scripts?: { sourceFunctionName?: string; invoker?: string }[] })[]) {
        if (e.duration < 200) continue;
        const s = e.scripts?.[0];
        diag('perf', 'long_frame', { ms: e.duration, blockingMs: e.blockingDuration ?? null, fn: (s?.sourceFunctionName || s?.invoker || '').slice(0, 60), crumb }, 'warn');
      }
    });
    po.observe({ type: 'long-animation-frame', buffered: true });
  } catch {
    /* not supported (Safari, Firefox) */
  }
}

/** iPhone stand-in for LoAF during a workout: frames over 250 ms, measured with requestAnimationFrame. */
export function jankProbe(): () => void {
  if (typeof requestAnimationFrame === 'undefined') return () => undefined;
  let last = performance.now();
  let id = 0;
  const tick = (t: number) => {
    const gap = t - last;
    last = t;
    if (gap > 250 && document.visibilityState === 'visible') diag('perf', 'long_frame', { ms: gap, crumb, via: 'raf' }, gap > 1000 ? 'warn' : 'info');
    id = requestAnimationFrame(tick);
  };
  id = requestAnimationFrame(tick);
  return () => cancelAnimationFrame(id);
}
